import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import type { Insight, InsightTone } from "../lib/budgetInsights";
import { categoryLabel, money, monthLabel } from "../lib/format";
import { ui } from "../theme";

// Resumen ejecutivo (#1332): la primera lámina de un informe de consultoría.
// Un veredicto en una frase, los hallazgos que lo explican —cada uno con su
// cifra— y qué hacer. El detalle va debajo, en secciones plegables.

const NS = "expensesStatements";

/** Parámetros que son cuentas o porcentajes, no dinero. */
const PLAIN_NUMBERS = new Set(["pct", "count", "months", "certainty", "coverage", "covered", "lines"]);

/** Texto de un hallazgo en el idioma de la interfaz, con sus cifras ya formateadas. */
export function useInsightText() {
  const { t, i18n } = useTranslation(["common", NS]);
  const language = i18n.language;
  return (group: "verdict" | "finding" | "action", insight: Insight) => {
    const params: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(insight.params)) {
      if (key === "category" || key === "to") params[key] = categoryLabel(String(value), language);
      else if (key.startsWith("month") && typeof value === "string") params[key] = monthLabel(value, language);
      else if (typeof value === "number") params[key] = PLAIN_NUMBERS.has(key) ? Math.round(value) : money(value, "MXN", language);
      else params[key] = value;
    }
    // Un veredicto de «se pasará» dice el mes solo cuando se conoce.
    const variant = group === "verdict" && insight.code === "willOverrun" && !("month" in insight.params) ? "willOverrunNoMonth" : insight.code;
    return t(`${NS}:budgetExec.${group}.${variant}`, params);
  };
}

const TONE: Record<InsightTone, { icon: string; text: string; band: string; dot: string }> = {
  good: { icon: "ri-checkbox-circle-fill", text: "text-emerald-600", band: "from-emerald-600 to-teal-700", dot: "bg-emerald-500" },
  warn: { icon: "ri-alert-fill", text: "text-amber-600", band: "from-amber-500 to-orange-600", dot: "bg-amber-500" },
  bad: { icon: "ri-error-warning-fill", text: "text-red-600", band: "from-red-600 to-rose-700", dot: "bg-red-500" },
  info: { icon: "ri-information-fill", text: "text-sky-600", band: "from-slate-600 to-slate-800", dot: "bg-sky-500" },
};

export default function ExecutiveSummary({
  title,
  scope,
  verdict,
  findings,
  actions,
  children,
  testId = "executive-summary",
}: {
  title: string;
  /** Periodo que cubre el resumen ("ago 2026 – oct 2026"). */
  scope: string;
  verdict: Insight;
  findings: Insight[];
  actions: Insight[];
  /** Indicadores clave, entre el veredicto y los hallazgos. */
  children?: ReactNode;
  testId?: string;
}) {
  const { t } = useTranslation(["common", NS]);
  const tx = (k: string, o?: Record<string, unknown>) => t(`${NS}:budgetExec.${k}`, o);
  const text = useInsightText();
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const tone = TONE[verdict.tone];

  return (
    <section aria-label={title} className="space-y-3" data-testid={testId}>
      <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${tone.band} p-5 text-white shadow-lg`} data-tone={verdict.tone}>
        <div className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-white/10" />
        <p className="relative text-xs font-medium uppercase tracking-wider text-white/75">
          {title} · {scope}
        </p>
        <p className="relative mt-1 text-xl font-bold leading-snug sm:text-2xl" data-testid="exec-verdict">
          <i className={`${tone.icon} mr-2`} />
          {text("verdict", verdict)}
        </p>
        <p className="relative mt-1 text-xs text-white/75">{tx(`status.${verdict.tone}`)}</p>
      </div>

      {children}

      <div className="grid gap-3 lg:grid-cols-2">
        <div className={c.card} data-testid="exec-findings">
          <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>
            <i className="ri-search-eye-line mr-1 text-teal-600" />
            {tx("findingsTitle")}
          </h3>
          {findings.length === 0 ? (
            <p className={`text-sm ${c.muted}`}>{tx("noFindings")}</p>
          ) : (
            <ul className="space-y-2">
              {findings.map((f, i) => (
                <li key={`${f.code}-${i}`} className={`flex gap-2 text-sm ${c.text}`} data-testid={`finding-${f.code}`}>
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${TONE[f.tone].dot}`} />
                  <span>{text("finding", f)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className={c.card} data-testid="exec-actions">
          <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>
            <i className="ri-compass-3-line mr-1 text-teal-600" />
            {tx("actionsTitle")}
          </h3>
          {actions.length === 0 ? (
            <p className={`text-sm ${c.muted}`}>{tx("noActions")}</p>
          ) : (
            <ol className="space-y-2">
              {actions.map((a, i) => (
                <li key={`${a.code}-${i}`} className={`flex gap-2 text-sm ${c.text}`} data-testid={`action-${a.code}`}>
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-600 text-[11px] font-semibold text-white">{i + 1}</span>
                  <span>{text("action", a)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </section>
  );
}

/** Sección de detalle plegable: título, una frase que dice qué muestra y el contenido. */
export function DetailSection({ id, title, takeaway, defaultOpen = false, children }: { id: string; title: string; takeaway: string; defaultOpen?: boolean; children: ReactNode }) {
  const { t } = useTranslation(["common", NS]);
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="space-y-3" data-testid={`detail-${id}`}>
      <button type="button" aria-expanded={open} aria-controls={open ? `detail-body-${id}` : undefined} onClick={() => setOpen((o) => !o)} className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left ${c.border} ${c.surface}`}>
        <i className={`${open ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} text-lg ${c.muted}`} />
        <span className="min-w-0 flex-1">
          <span className={`block text-sm font-semibold ${c.text}`}>{title}</span>
          <span className={`block text-xs ${c.muted}`}>{takeaway}</span>
        </span>
        <span className="shrink-0 text-xs text-teal-600">{t(`${NS}:budgetExec.${open ? "hide" : "show"}`)}</span>
      </button>
      {open && <div id={`detail-body-${id}`} className="space-y-4">{children}</div>}
    </section>
  );
}
